import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IncidentsCard } from "./IncidentsCard";

export default function HsTab() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const [hazards, setHazards] = useState([]);
  const [plan, setPlan] = useState(null);
  const [hazardForm, setHazardForm] = useState({ hazard: "", risk_level: "Medium", controls: "", responsible: "" });
  const [editing, setEditing] = useState(null);  // hazard being edited inline
  const [msg, setMsg] = useState("");

  async function load() {
    const [h, p] = await Promise.all([
      api.get(`/projects/${project.id}/hs/hazards`),
      api.get(`/projects/${project.id}/hs/safety-plan`),
    ]);
    setHazards(h.data); setPlan(p.data);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  async function addHazard(e) {
    e.preventDefault();
    await api.post("/hazards", { ...hazardForm, project_id: project.id, active: true });
    setHazardForm({ hazard: "", risk_level: "Medium", controls: "", responsible: "" }); load();
    setMsg("Hazard added ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function saveEdit() {
    await api.patch(`/hazards/${editing.id}`, editing);
    setEditing(null); load(); setMsg("Hazard saved ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function delHazard(h) {
    if (!window.confirm(`Delete hazard "${h.hazard}"? Are you sure?`)) return;
    await api.delete(`/hazards/${h.id}`); load(); setMsg("Hazard deleted ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function savePlan() {
    await api.put(`/projects/${project.id}/hs/safety-plan`, plan);
    setMsg("Safety plan saved ✓"); setTimeout(()=>setMsg(""), 2000);
  }
  async function printPack() {
    const r = await api.get(`/projects/${project.id}/hs/print-pack`, { responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  }

  const canEdit = user?.role === "Admin" || user?.role === "Manager";

  return (
    <div className="space-y-3" data-testid="hs-tab">
      {msg && <div className="text-green-400 text-sm" data-testid="hs-msg">{msg}</div>}
      <div className="card">
        <div className="flex justify-between items-center">
          <h2 className="text-silver">Hazard Register</h2>
          <a className="btn-silver inline-block" href="#"
             onClick={(e) => { e.preventDefault(); printPack(); }}
             data-testid="btn-hs-print-pack">Download Print Pack (PDF)</a>
        </div>
        {canEdit && (
          <form onSubmit={addHazard} className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-3">
            <input className="input" placeholder="Hazard" value={hazardForm.hazard} onChange={e=>setHazardForm({...hazardForm,hazard:e.target.value})}/>
            <select className="input" value={hazardForm.risk_level} onChange={e=>setHazardForm({...hazardForm,risk_level:e.target.value})}>
              <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
            </select>
            <input className="input" placeholder="Controls" value={hazardForm.controls} onChange={e=>setHazardForm({...hazardForm,controls:e.target.value})}/>
            <input className="input" placeholder="Responsible" value={hazardForm.responsible} onChange={e=>setHazardForm({...hazardForm,responsible:e.target.value})}/>
            <button className="btn-silver" data-testid="btn-add-hazard">Add hazard</button>
          </form>
        )}
        <table className="table mt-3"><thead><tr><th>Hazard</th><th>Risk</th><th>Controls</th><th>Responsible</th>{canEdit && <th></th>}</tr></thead>
          <tbody>{hazards.map(h => editing?.id === h.id ? (
            <tr key={h.id} data-testid={`hz-edit-${h.id}`}>
              <td><input className="input" value={editing.hazard} onChange={e=>setEditing({...editing, hazard: e.target.value})}/></td>
              <td><select className="input" value={editing.risk_level} onChange={e=>setEditing({...editing, risk_level: e.target.value})}>
                <option>Low</option><option>Medium</option><option>High</option><option>Critical</option>
              </select></td>
              <td><input className="input" value={editing.controls||""} onChange={e=>setEditing({...editing, controls: e.target.value})}/></td>
              <td><input className="input" value={editing.responsible||""} onChange={e=>setEditing({...editing, responsible: e.target.value})}/></td>
              <td className="flex gap-2 text-xs">
                <button className="hub-link" onClick={saveEdit} data-testid={`btn-hz-save-${h.id}`}>Save</button>
                <button className="opacity-70" onClick={()=>setEditing(null)}>Cancel</button>
              </td>
            </tr>
          ) : (
            <tr key={h.id} data-testid={`hz-row-${h.id}`}><td>{h.hazard}{h.needs_review && <span className="ml-2 text-[10px] text-amber-300 border border-amber-300 px-1 rounded" data-testid={`hz-review-${h.id}`}>Reported by {h.reported_by || "worker"}, needs review</span>}</td>
              <td><span className={`badge ${h.risk_level==="Critical"?"badge-red":h.risk_level==="High"?"badge-amber":"badge-grey"}`}>{h.risk_level}</span></td>
              <td>{h.controls}</td><td>{h.responsible}</td>
              {canEdit && (
                <td className="flex gap-2 text-xs">
                  <button className="hub-link" onClick={()=>setEditing({...h})} data-testid={`btn-hz-edit-${h.id}`}>Edit</button>
                  <button className="text-red-400" onClick={()=>delHazard(h)} data-testid={`btn-hz-del-${h.id}`}>Delete</button>
                </td>
              )}
            </tr>
          ))}
          {hazards.length === 0 && <tr><td colSpan={canEdit?"5":"4"} className="opacity-50">No hazards recorded.</td></tr>}
          </tbody></table>
      </div>

      {canEdit && <IncidentsCard projectId={project.id} />}

      {plan && canEdit && (
        <div className="card">
          <h2 className="text-silver mb-2">Safety Plan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">Nearest hospital<input className="input" value={plan.nearest_hospital||""} onChange={e=>setPlan({...plan,nearest_hospital:e.target.value})}/></label>
            <label className="text-sm">Assembly point<input className="input" value={plan.assembly_point||""} onChange={e=>setPlan({...plan,assembly_point:e.target.value})}/></label>
            <label className="text-sm md:col-span-2">First aid location<input className="input" value={plan.first_aid_location||""} onChange={e=>setPlan({...plan,first_aid_location:e.target.value})}/></label>
            <label className="text-sm md:col-span-2">PPE (comma separated)
              <input className="input" value={(plan.ppe||[]).join(", ")} onChange={e=>setPlan({...plan,ppe:e.target.value.split(",").map(s=>s.trim()).filter(Boolean)})}/></label>
          </div>
          <button className="btn-silver mt-3" onClick={savePlan}>Save safety plan</button>
        </div>
      )}
    </div>
  );
}
